// The device's half of online play (2026-11-01): the turns the room sent, and the one way they reach the sim. Pure: no
// DOM, no socket. main.ts feeds it what client.ts receives and asks it, every frame, how far the sim may step;
// tests/net.test.ts runs four of these against a RoomCore and checks every device ends on the same hash.
// Order inside a tick, the same on every device and the same as a match against bots (main.ts stepSim):
//   1. the turn's seat changes (a bot takes over a seat, or hands it back), in the order the room sent them
//   2. the turn's commands, in the order the room took them, each cleaned again (protocol.ts cleanCommand)
//   3. every bot thinks, in seat order
//   4. sim.step()
import { Bot } from '../sim/bot';
import { DT } from '../sim/defs';
import { PLAYERS, type Command, type Sim } from '../sim/sim';
import { cleanCommand, type Seat, type Start, type Turn } from './protocol';

/** The bots a match starts with: one for every seat no person holds, made in seat order (each draws on the sim's Rng,
 *  so every device must make them in the same order). */
export function startBots(sim: Sim, seats: readonly Seat[]): Bot[] {
  const bots: Bot[] = [];
  for (let p = 0; p < PLAYERS; p++) if (!seats[p].human) bots.push(new Bot(sim, p));
  return bots;
}

/** A bot for a seat a person left mid-match: it keeps their army and hero. */
export function takeOver(sim: Sim, p: number): Bot {
  const pl = sim.players[p];
  return new Bot(sim, p, pl.faction ?? undefined, undefined, pl.hero?.id ?? undefined);
}

/** A person takes seat p from its bot (the bot steps out; the army, hero and gold stay). Online that is a seat coming back
 *  to its person; offline, a Jump in from the landing taking a bot's corner (client/jumpmatch.ts). */
export function humanTakes(bots: Bot[], p: number) {
  const at = bots.findIndex((b) => b.p === p);
  if (at >= 0) bots.splice(at, 1);
}

export class Lockstep {
  /** Turns that carried something, by number. */
  private turns = new Map<number, Turn>();
  /** Every turn up to and including this one has arrived (-1: none yet). */
  latest = -1;

  constructor(readonly start: Start) {}

  /** The log a (re)joining device gets: turns from `from`, and every turn below `at` sent. */
  load(turns: readonly Turn[], at: number) {
    for (const t of turns) this.keep(t);
    this.latest = Math.max(this.latest, at - 1);
  }

  /** A turn as it arrives. Turns come in order on one socket; a gap means a lost message, which a socket never has. */
  add(t: Turn) {
    this.keep(t);
    if (t.t > this.latest) this.latest = t.t;
  }

  private keep(t: Turn) { if (t.c?.length || t.e?.length) this.turns.set(t.t, t); }

  /** Whether the sim may take its next step (step number sim.ticks). */
  ready(sim: Sim) { return sim.ticks <= this.latest; }
  /** Steps the sim is behind the room. */
  behind(sim: Sim) { return this.latest + 1 - sim.ticks; }

  /** Apply turn sim.ticks to the sim, then run the bots; the caller steps the sim right after. `bots` is changed in
   *  place (a takeover adds one, a return removes one). Returns the turn's seat changes for the caller to show. */
  /** `refused`: a command the sim would not take on its turn (a skill with nothing in reach by then), so the page can
   *  say so for its own seat as it would offline (Flash F5). */
  apply(sim: Sim, bots: Bot[], refused?: (p: number, c: Command) => void) {
    const turn = this.turns.get(sim.ticks);
    if (turn) {
      this.turns.delete(sim.ticks);
      for (const e of turn.e ?? []) {
        const at = bots.findIndex((b) => b.p === e.p);
        if (e.to === 'bot' && at < 0) { bots.push(takeOver(sim, e.p)); bots.sort((a, b) => a.p - b.p); }
        if (e.to === 'human') humanTakes(bots, e.p);
      }
      for (const [p, raw] of turn.c ?? []) {
        const c = cleanCommand(raw);
        if (c && Number.isInteger(p) && p >= 0 && p < PLAYERS && !bots.some((b) => b.p === p) && !sim.command(p, c)) refused?.(p, c);
      }
    }
    for (const b of bots) b.tick(DT);
    return turn?.e ?? [];
  }
}
